import {
  NEW_FACILITY_KEY,
  NewFacilityOption,
  storageEnvTypeOptions,
  WEIGHT_UNIT_TYPES_OPTIONs
} from "../../../../../config/constants";
import { IngredientDataTypesOption } from "../../../../../config/enums/IngredientType.enum";
import { MEASUREMENT_TYPES } from "../../../../../config/enums/MeasurementTypes";
import { StorageEnvType } from "../../../../../config/enums/StorageType.enum";
import { getInternalStorageVariantConfig } from "./internalStorageVariant.config";
import {
  InternalStorageFormValues,
  InternalStorageSearchFacilityOption,
  InternalStorageSelectOption,
  InternalStorageToolVariant
} from "./internalStorage.types";

export const isSpecificDataType = (dataType?: string) =>
  dataType === IngredientDataTypesOption.SPECIFIC;

export const buildInternalStorageNodeLabel = (internalStorageName: string) =>
  `Internal Storage (${internalStorageName})`;

export const mapDropdownFacilities = (
  facilities: any[] = [],
  includeNewFacility = false
): InternalStorageSelectOption[] => {
  const list = includeNewFacility ? [...facilities, NewFacilityOption] : facilities;
  return list.map(item => ({
    key: item.value ?? item.id,
    value: item.value ?? item.id,
    text: item.text ?? item.title
  }));
};

export const mapSearchFacilities = (
  facilities: any[] = []
): InternalStorageSearchFacilityOption[] =>
  facilities.map(item => ({
    id: item.id,
    title: item.title,
    value: item.id
  }));

export const getStorageEnvLabel = (value?: number) =>
  storageEnvTypeOptions.find(option => option.value === value)?.text || "";

export const getWeightUnitLabel = (value?: number) =>
  WEIGHT_UNIT_TYPES_OPTIONs.find(option => option.value === value)?.text || "";

export const isNewFacilitySelection = (value?: string) => value === NEW_FACILITY_KEY;

export const resolveFacilityViewId = (value: unknown): string | null => {
  if (value === undefined || value === null || value === "") {
    return null;
  }

  const facilityId = String(value);
  return isNewFacilitySelection(facilityId) ? null : facilityId;
};

export const buildDefaultFormValues = (
  currentData: any,
  defaultFacilityId?: string,
  variant: InternalStorageToolVariant = "mapping"
): InternalStorageFormValues => {
  const features = getInternalStorageVariantConfig(variant);
  const dataType = features.supportsDataType
    ? currentData?.dataType || IngredientDataTypesOption.SPECIFIC
    : IngredientDataTypesOption.SPECIFIC;

  return {
    internalStorageName: currentData?.internalStorageName || "",
    dataType,
    storageEnv: features.supportsStorageEnvironment
      ? currentData?.storageEnv || StorageEnvType.AMBIENT
      : StorageEnvType.AMBIENT,
    warehouseName: currentData?.facilityId || defaultFacilityId || "",
    temperature: currentData?.storageTemperature ?? "",
    warehouseSize: currentData?.warehouseSize ?? "",
    weight: currentData?.weight ?? "",
    weightUnit: features.supportsWeightUnit
      ? currentData?.weightUnit || MEASUREMENT_TYPES.WEIGHT_KG
      : MEASUREMENT_TYPES.WEIGHT_KG,
    electricity: currentData?.electricity ?? "",
    areaUtilization:
      currentData?.utilizationOfWarehouse ?? (isSpecificDataType(dataType) ? 100 : 0),
    storageDays: currentData?.storageDays ?? ""
  };
};

export const mapFormValuesToLoadedData = (
  values: InternalStorageFormValues,
  storageId: string,
  variant: InternalStorageToolVariant = "mapping"
) => {
  const features = getInternalStorageVariantConfig(variant);
  const specificDataType = features.supportsDataType
    ? isSpecificDataType(values.dataType)
    : true;

  return {
    _id: storageId,
    internalStorageName: values.internalStorageName,
    facilityId: values.warehouseName,
    warehouseSize: specificDataType ? Number(values.warehouseSize) || 0 : 0,
    storageTemperature: Number(values.temperature) || 0,
    weight: Number(values.weight) || 0,
    electricity: specificDataType ? Number(values.electricity) || 0 : 0,
    utilizationOfWarehouse: specificDataType ? Number(values.areaUtilization) || 0 : 0,
    storageDays: Number(values.storageDays) || 0,
    dataType: values.dataType,
    storageEnv: Number(values.storageEnv) || StorageEnvType.AMBIENT,
    weightUnit: features.supportsWeightUnit
      ? Number(values.weightUnit) || MEASUREMENT_TYPES.WEIGHT_KG
      : MEASUREMENT_TYPES.WEIGHT_KG
  };
};

export interface BuildInternalStoragePayloadParams {
  values: InternalStorageFormValues;
  variant: InternalStorageToolVariant;
  gateId: string;
  internalStorageId?: string | null;
  originalStorageName?: string;
}

export const buildInternalStoragePayload = ({
  values,
  variant,
  gateId,
  internalStorageId,
  originalStorageName
}: BuildInternalStoragePayloadParams) => {
  const features = getInternalStorageVariantConfig(variant);
  const specificDataType = features.supportsDataType
    ? isSpecificDataType(values.dataType)
    : true;

  const storageData: Record<string, unknown> = {
    internalStorageName: originalStorageName || values.internalStorageName || "",
    facilityId: values.warehouseName || "",
    warehouseSize: specificDataType ? Number(values.warehouseSize) || 0 : 0,
    storageTemperature: Number(values.temperature) || 0,
    weight: Number(values.weight) || 0,
    electricity: specificDataType ? Number(values.electricity) || 0 : 0,
    utilizationOfWarehouse: specificDataType ? Number(values.areaUtilization) || 0 : 0,
    storageDays: Number(values.storageDays) || 0
  };

  if (features.supportsDataType) {
    storageData.dataType = values.dataType || IngredientDataTypesOption.SPECIFIC;
  }

  if (features.supportsStorageEnvironment) {
    storageData.storageEnv = Number(values.storageEnv) || StorageEnvType.AMBIENT;
  }

  if (features.supportsWeightUnit) {
    storageData.weightUnit = Number(values.weightUnit) || MEASUREMENT_TYPES.WEIGHT_KG;
  }

  if (variant === "simulate") {
    storageData.changeStatus = true;
  }

  const payload: Record<string, unknown> = {
    storageData
  };

  if (gateId) {
    payload.farmGateId = gateId;
    payload.processGateId = gateId;
  }

  if (internalStorageId) {
    payload.internalStorageId = internalStorageId;
  }

  return payload;
};

export const normalizeInternalStoragePayload = (
  payload: Record<string, unknown>,
  gateIdKey: "farmGateId" | "processGateId",
  gateId: string
) => {
  const normalized = { ...payload };
  delete normalized.farmGateId;
  delete normalized.processGateId;
  normalized[gateIdKey] = gateId;
  return normalized;
};

export const mapOriginalStorageData = (originalData: any) => {
  if (!originalData) {
    return null;
  }

  return {
    internalStorageName: originalData.internalStorageName,
    dataType: originalData.dataType,
    storageEnv: originalData.storageEnv,
    facilityId: originalData.facilityId,
    storageTemperature: originalData.storageTemperature,
    warehouseSize: originalData.warehouseSize,
    weight: originalData.weight,
    weightUnit: originalData.weightUnit,
    electricity: originalData.electricity,
    utilizationOfWarehouse: originalData.utilizationOfWarehouse,
    storageDays: originalData.storageDays
  };
};
